import Dexie, { type Table } from 'dexie';
import type { AppState } from '../types/state';

export interface TelemetrySample {
  id?: number;
  timestamp: Date;
  profileKey: string;
  frequency: number;
  current: number;
  dcVoltage: number;
  temperature: number;
  load: number;
}

export interface AlarmEvent {
  id?: number;
  timestamp: Date;
  deviceId: number;
  alarmCode: string;
  severity: 'critical' | 'warning' | 'info';
  acknowledged: boolean;
  acknowledgedAt?: Date;
  clearedAt?: Date;
}

export class Database extends Dexie {
  telemetry!: Table<TelemetrySample>;
  alarms!: Table<AlarmEvent>;
  state!: Table<{ id: string; data: AppState }>;

  constructor() {
    super('PREVDRIVEDB');
    this.version(1).stores({
      telemetry: '++id, timestamp, profileKey',
      alarms: '++id, timestamp, deviceId, severity, acknowledged',
      state: 'id',
    });
  }

  async saveState(appState: AppState): Promise<void> {
    await this.state.put({ id: 'current', data: appState });
  }

  async loadState(): Promise<AppState | undefined> {
    const entry = await this.state.get('current');
    return entry?.data;
  }

  async recordSample(sample: TelemetrySample): Promise<void> {
    await this.telemetry.add(sample);
    const cutoff = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    await this.telemetry.where('timestamp').below(cutoff).delete();
  }

  async recordAlarm(alarm: AlarmEvent): Promise<void> {
    await this.alarms.add(alarm);
  }

  async acknowledgeAlarm(id: number): Promise<void> {
    await this.alarms.update(id, {
      acknowledged: true,
      acknowledgedAt: new Date(),
    });
  }
}
